import type { AttributeRefusal, AttributeSpend } from "odyssey-shared";
import { NO_PENDING, addPoint, pendingAfter, pendingTotal, removePoint, spendsOf, type AttributesFrame, type Pending } from "./allocation.ts";
import type { CharacterTabId } from "./tabs.ts";

/**
 * The character window's state (ADR-138's amendment of 2026-10-31, P2.22-T25, P2.26-T24): whether it is open and on
 * which tab, the last `attributes` frame, the pending choice, the dialog open, a request waiting for its answer, the
 * last refusal, and the character's companions as the server last told them. Nothing here leads the server: a spend,
 * a respec and a companion's out or home change only when the server's frame says so.
 */

/**
 * A companion's bond as the page is told it (the author's bonding ruling, 2026-10-31): the tier word, the next tier's
 * word or null, and the pips, the tier's place from 0 among how many tiers (the shape of `StandingTierView` on the bond
 * branch); `exact`, the fraction of the way to the next tier, only for a character with Animal Lore, null otherwise.
 * The standing itself never reaches the page.
 */
export interface CompanionBond {
  readonly tier: string;
  readonly next: string | null;
  readonly pips: { readonly at: number; readonly of: number };
  readonly exact: number | null;
}

/**
 * One companion as the Companions tab draws it: its id, its kind (`COMPANION_KINDS`), its name, whether it is out, its
 * bond and today's feed state (null unknown).
 */
export interface CompanionView {
  readonly id: number;
  readonly kind: number;
  readonly name: string;
  readonly out: boolean;
  readonly bond: CompanionBond | null;
  readonly fed: boolean | null;
}

/**
 * Which dialog stands over the window: none, the Spend confirmation or the Respec confirmation.
 */
export type CharacterDialog = "none" | "spend" | "respec";

/**
 * What the window reads.
 */
export interface CharacterSnapshot {
  readonly open: boolean;
  readonly tab: CharacterTabId;
  readonly frame: AttributesFrame | null;
  readonly pending: Pending;
  readonly dialog: CharacterDialog;
  readonly waiting: boolean;
  readonly refusal: AttributeRefusal | null;
  readonly companions: readonly CompanionView[];
  readonly selected: number | null;
  /** The companion whose out or home request waits for the server's state, or null. */
  readonly asking: number | null;
  /** Whether a Game Master holds the character, which disables the requests. */
  readonly frozen: boolean;
}

/**
 * Where the window's requests go: the connection's senders, each answering whether the request was sent.
 */
export interface CharacterSends {
  readonly spend: (spends: readonly AttributeSpend[]) => boolean;
  readonly respec: () => boolean;
  readonly companionSet: (out: boolean) => boolean;
}

/**
 * How long a request waits for its answer before the window stops waiting and says so: the server answers every
 * request, a refusal included, so this only ends a wait whose answer was lost.
 */
export const ANSWER_WAIT_MS = 5000;

/**
 * The timers the store's wait runs on, injected so tests run time by hand.
 */
export interface WaitTimers {
  readonly set: (callback: () => void, delayMs: number) => unknown;
  readonly clear: (handle: unknown) => void;
}

const PAGE_TIMERS: WaitTimers = { set: (callback, delayMs) => setTimeout(callback, delayMs), clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>) };

const START: CharacterSnapshot = Object.freeze({ open: false, tab: "character", frame: null, pending: NO_PENDING, dialog: "none", waiting: false, refusal: null, companions: Object.freeze([]), selected: null, asking: null, frozen: false });

/**
 * The store.
 */
export class CharacterStore {
  private readonly listeners = new Set<() => void>();
  private snapshot: CharacterSnapshot = START;
  private sends: CharacterSends | null = null;
  private wait: unknown = null;

  /**
   * @param cap `attribute_cap`, which + stops at.
   * @param timers The timers a request's wait runs on; the page's by default.
   */
  public constructor(
    private readonly cap: number,
    private readonly timers: WaitTimers = PAGE_TIMERS,
  ) {}

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window reads, the same object until something changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): CharacterSnapshot => this.snapshot;

  /**
   * Binds the connection's senders.
   *
   * @param sends The senders, or null while no session is open.
   */
  public bindSends(sends: CharacterSends | null): void {
    this.sends = sends;
  }

  /**
   * Opens or closes the window; closing drops the pending choice and any dialog.
   *
   * @param open Whether it is open.
   */
  public setOpen(open: boolean): void {
    if (open === this.snapshot.open) {
      return;
    }
    if (!open) {
      this.stopWaiting();
    }
    this.change(open ? { open } : { open, pending: NO_PENDING, dialog: "none", refusal: null, waiting: false });
  }

  /**
   * Opens a closed window and closes an open one.
   */
  public toggle(): void {
    this.setOpen(!this.snapshot.open);
  }

  /**
   * Shows a tab.
   *
   * @param tab The tab.
   */
  public setTab(tab: CharacterTabId): void {
    if (tab !== this.snapshot.tab) {
      this.change({ tab, dialog: "none" });
    }
  }

  /**
   * Takes an `attributes` frame: the frame replaces the last, a pending choice survives only while it still fits, and a
   * request waiting is answered.
   *
   * @param frame The frame.
   */
  public receive(frame: AttributesFrame): void {
    const pending = pendingAfter(this.snapshot.frame, frame, this.snapshot.pending);
    this.stopWaiting();
    this.change({ frame, pending, waiting: false, dialog: this.snapshot.waiting ? "none" : this.snapshot.dialog, refusal: null });
  }

  /**
   * Takes an `attributeRefused`: the request waiting is answered and the reason shown; the pending choice stays.
   *
   * @param reason The reason.
   */
  public refused(reason: AttributeRefusal): void {
    this.stopWaiting();
    this.change({ waiting: false, dialog: "none", refusal: reason });
  }

  /**
   * Adds a pending point.
   *
   * @param index The attribute's place in `ATTRIBUTES`.
   */
  public add(index: number): void {
    const { frame, pending, waiting } = this.snapshot;
    if (frame !== null && !waiting) {
      const next = addPoint(frame, pending, index, this.cap);
      if (next !== pending) {
        this.change({ pending: next, refusal: null });
      }
    }
  }

  /**
   * Takes a pending point back.
   *
   * @param index The attribute's place.
   */
  public remove(index: number): void {
    const { pending, waiting } = this.snapshot;
    const next = removePoint(pending, index);
    if (next !== pending && !waiting) {
      this.change({ pending: next, refusal: null });
    }
  }

  /**
   * Clears the pending choice.
   */
  public reset(): void {
    if (pendingTotal(this.snapshot.pending) > 0 && !this.snapshot.waiting) {
      this.change({ pending: NO_PENDING, refusal: null });
    }
  }

  /**
   * Opens a confirmation dialog, or closes it.
   *
   * @param dialog The dialog.
   */
  public ask(dialog: CharacterDialog): void {
    if (dialog !== this.snapshot.dialog && !this.snapshot.waiting) {
      this.change({ dialog });
    }
  }

  /**
   * Sends the pending choice as one `spendAttributes`, from the Spend dialog.
   */
  public confirmSpend(): void {
    const spends = spendsOf(this.snapshot.pending);
    if (spends.length > 0 && !this.snapshot.waiting && this.sends?.spend(spends) === true) {
      this.startWaiting();
    }
  }

  /**
   * Sends a `respecAttributes`, from the Respec dialog; the pending choice stays until the answer, which drops it on a
   * respec that took and keeps it on a refusal.
   */
  public confirmRespec(): void {
    if (!this.snapshot.waiting && this.sends?.respec() === true) {
      this.startWaiting();
    }
  }

  /**
   * Replaces the character's companions as the server told them; a request waiting on a companion is answered.
   *
   * @param companions The companions.
   */
  public setCompanions(companions: readonly CompanionView[]): void {
    const selected = companions.some((entry) => entry.id === this.snapshot.selected) ? this.snapshot.selected : (companions[0]?.id ?? null);
    this.change({ companions, selected, asking: null });
  }

  /**
   * Selects a companion on the rail.
   *
   * @param id The companion's id.
   */
  public select(id: number): void {
    if (id !== this.snapshot.selected) {
      this.change({ selected: id });
    }
  }

  /**
   * Asks the server to send the selected companion out or home; the control keeps showing the server's state.
   *
   * @param out Whether it should be out.
   */
  public setOut(out: boolean): void {
    const { selected, asking, companions, frozen } = this.snapshot;
    const companion = companions.find((entry) => entry.id === selected);
    if (companion === undefined || asking !== null || frozen || companion.out === out) {
      return;
    }
    if (this.sends?.companionSet(out) === true) {
      this.change({ asking: companion.id });
    }
  }

  /**
   * Marks the character held by a Game Master, or free.
   *
   * @param frozen Whether it is held.
   */
  public setFrozen(frozen: boolean): void {
    if (frozen !== this.snapshot.frozen) {
      this.change({ frozen });
    }
  }

  /**
   * Forgets the session's state as it ends; the window closes.
   */
  public resetSession(): void {
    this.stopWaiting();
    this.change({ ...START, tab: this.snapshot.tab });
  }

  /**
   * Waits for a request's answer: the window is waiting until the answer, or until `ANSWER_WAIT_MS` pass with none,
   * when it stops waiting and says `busy`, keeping the pending choice.
   */
  private startWaiting(): void {
    this.stopWaiting();
    this.wait = this.timers.set(() => {
      this.wait = null;
      this.change({ waiting: false, dialog: "none", refusal: "busy" });
    }, ANSWER_WAIT_MS);
    this.change({ waiting: true });
  }

  /**
   * Ends a request's wait.
   */
  private stopWaiting(): void {
    if (this.wait !== null) {
      this.timers.clear(this.wait);
      this.wait = null;
    }
  }

  /**
   * Replaces part of the snapshot and tells the listeners.
   *
   * @param part The fields that change.
   */
  private change(part: Partial<CharacterSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...part };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
